import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, errorMessage } from "@/lib/api";
import { accessDeniedNavigateState } from "@/utils/accessDenied";
import {
  EVENT_TYPES,
  dayKey,
  modeLabel,
  monthLabel,
  shortDay,
  timeRange,
  todayKey,
  typeLabel,
} from "@/utils/eventFormat";
import "./EventPages.css";

/**
 * Staff list for page_access.events. One GET /api/events?school_id= per the
 * schools in scope (Learn passes them to UIS). Upcoming / Past and the type
 * filter run on the loaded list, so no extra query params are needed.
 */

type SchoolEvent = {
  id: string;
  title: string;
  type: string;
  mode: string;
  start_date: string | null;
  start_time: string | null;
  end_date?: string | null;
  end_time?: string | null;
  location: string | null;
  require_rsvp?: boolean;
  max_capacity?: number | null;
};

type SchoolOption = { id: string; name: string };

type ListData = {
  events: SchoolEvent[];
  schools: SchoolOption[];
  schoolId: string | null;
};

type View = "upcoming" | "past";

function lastDay(event: SchoolEvent) {
  return dayKey(event.end_date) || dayKey(event.start_date);
}

function sortKey(event: SchoolEvent) {
  return `${dayKey(event.start_date) || "9999-99-99"} ${event.start_time || "99:99"}`;
}

function modeNote(event: SchoolEvent) {
  const bits: string[] = [];
  if (event.require_rsvp && event.mode !== "attendance") bits.push("RSVP required");
  if (event.max_capacity != null) bits.push(`Limit ${event.max_capacity}`);
  return bits.join(" · ");
}

export default function Events() {
  const { pageAccess, session, loading: authLoading } = useAuth();
  const canOpen = Boolean(pageAccess?.events);
  const canInsert = Boolean(session?.acuityLearn?.eventActions?.insert);
  const [schoolId, setSchoolId] = useState("");
  const [data, setData] = useState<ListData | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>("upcoming");
  const [type, setType] = useState("");
  const typeId = useId();
  const schoolSelectId = useId();

  const load = useCallback(async (nextSchoolId: string) => {
    setLoading(true);
    setError("");
    try {
      const query = nextSchoolId ? `?school_id=${encodeURIComponent(nextSchoolId)}` : "";
      const envelope = await api<{ success: boolean; data: ListData }>(`/api/events${query}`);
      const payload = envelope.data || { events: [], schools: [], schoolId: null };
      setData(payload);
      if (!nextSchoolId && payload.schoolId) setSchoolId(payload.schoolId);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading || !canOpen) return;
    void load(schoolId);
  }, [authLoading, canOpen, load, schoolId]);

  const groups = useMemo(() => {
    const today = todayKey();
    const events = (data?.events || []).filter((event) => !type || event.type === type);
    const shown = events.filter((event) => {
      const end = lastDay(event);
      const past = Boolean(end) && end < today;
      return view === "past" ? past : !past;
    });
    shown.sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
    if (view === "past") shown.reverse();
    const byMonth = new Map<string, SchoolEvent[]>();
    for (const event of shown) {
      const label = monthLabel(event.start_date);
      byMonth.set(label, [...(byMonth.get(label) || []), event]);
    }
    return [...byMonth.entries()].map(([month, items]) => ({ month, items }));
  }, [data, type, view]);

  if (authLoading) return null;
  if (!canOpen) return <Navigate to="/" replace state={accessDeniedNavigateState("events")} />;

  const schools = data?.schools || [];
  const count = groups.reduce((sum, group) => sum + group.items.length, 0);

  return (
    <div className="al-ev">
      <header className="al-ev-head">
        <div>
          <h1>Events</h1>
          <p className="al-ev-sub">Conferences, assemblies, volunteer days, and appointments.</p>
        </div>
        {canInsert ? (
          <Link className="al-ev-primary" to="/events/new">
            <Plus size={16} aria-hidden="true" />
            New event
          </Link>
        ) : null}
      </header>

      <div className="al-ev-toolbar">
        <div className="al-ev-tabs" role="tablist" aria-label="Which events">
          {(["upcoming", "past"] as View[]).map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={view === value}
              className={view === value ? "is-on" : ""}
              onClick={() => setView(value)}
            >
              {value === "upcoming" ? "Upcoming" : "Past"}
            </button>
          ))}
        </div>
        <div className="al-ev-filters">
          <label className="al-ev-sr" htmlFor={typeId}>
            Type
          </label>
          <select id={typeId} value={type} onChange={(event) => setType(event.target.value)}>
            <option value="">All types</option>
            {EVENT_TYPES.map((value) => (
              <option key={value} value={value}>
                {typeLabel(value)}
              </option>
            ))}
          </select>
          {schools.length > 1 ? (
            <>
              <label className="al-ev-sr" htmlFor={schoolSelectId}>
                School
              </label>
              <select
                id={schoolSelectId}
                value={schoolId}
                onChange={(event) => setSchoolId(event.target.value)}
              >
                <option value="">All schools</option>
                {schools.map((school) => (
                  <option key={school.id} value={school.id}>
                    {school.name}
                  </option>
                ))}
              </select>
            </>
          ) : null}
        </div>
      </div>

      {error ? (
        <p className="al-ev-error" role="alert">
          {error}
        </p>
      ) : null}
      {loading ? <p className="al-ev-note">Loading events…</p> : null}
      {!loading && !error && count === 0 ? (
        <p className="al-ev-empty">
          {view === "upcoming" ? "No upcoming events." : "No past events."}
          {view === "upcoming" && canInsert ? (
            <>
              {" "}
              <Link to="/events/new">Create one</Link>.
            </>
          ) : null}
        </p>
      ) : null}

      {!loading && count > 0 ? (
        <div className="al-ev-scroll">
          <table className="al-ev-table al-ev-list">
            <caption className="al-ev-sr">{view === "upcoming" ? "Upcoming events" : "Past events"}</caption>
            <thead>
              <tr>
                <th scope="col" className="al-ev-col-date">
                  Date
                </th>
                <th scope="col">Event</th>
                <th scope="col" className="al-ev-col-mode">
                  Mode
                </th>
                <th scope="col">Location</th>
              </tr>
            </thead>
            {groups.map((group) => (
              <tbody key={group.month}>
                <tr className="al-ev-month">
                  <th scope="colgroup" colSpan={4}>
                    {group.month}
                  </th>
                </tr>
                {group.items.map((event) => {
                  const note = modeNote(event);
                  return (
                    <tr key={event.id}>
                      <td className="al-ev-col-date">
                        <span className="al-ev-strong">{shortDay(event.start_date) || "No date"}</span>
                        {timeRange(event) ? <span className="al-ev-muted">{timeRange(event)}</span> : null}
                      </td>
                      <td>
                        <Link className="al-ev-title" to={`/events/${encodeURIComponent(event.id)}`}>
                          {event.title}
                        </Link>
                        <span className="al-ev-muted">{typeLabel(event.type)}</span>
                      </td>
                      <td className="al-ev-col-mode">
                        <span>{modeLabel(event.mode)}</span>
                        {note ? <span className="al-ev-muted">{note}</span> : null}
                      </td>
                      <td>{event.location || <span className="al-ev-faint">—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            ))}
          </table>
        </div>
      ) : null}

      {!loading && count > 0 ? (
        <p className="al-ev-note al-ev-foot">
          {count} {view === "upcoming" ? "upcoming" : "past"} {count === 1 ? "event" : "events"}
        </p>
      ) : null}
    </div>
  );
}
